import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Button, Space, Spin } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { makeGetCall } from '../../utils/helper';
import { resolveTemplateModalities } from '../../utils/modalityAssignment';
import { REPORT_CONFIG } from '../Reporting/constants';
import { fetchTemplates } from './documentsApi';

/** When off (the default), the body part dropdown starts empty and the reporting
 *  radiologist picks it — the order's po_body_part is ignored for pre-selection. */
const AUTO_SELECT_BODY_PART = REPORT_CONFIG.AUTO_SELECT_NODE_FROM_BODYPART;

interface TemplatePanelProps {
  pacsOrdId: string | number;
  modality?: string;
  /** Only consulted when AUTO_SELECT_NODE_FROM_BODYPART is on. */
  bodyPart?: string;
  onInsertTemplate: (templateId: number) => void;
  /** 'panel' = compact full-width single row for a side panel (no icon, no Load button —
   *  selecting a template applies it directly). 'inline' = the original toolbar row. */
  variant?: 'inline' | 'panel';
}

export default function TemplatePanel({ pacsOrdId, modality, bodyPart, onInsertTemplate, variant = 'inline' }: TemplatePanelProps) {
  const [bodyPartsList, setBodyPartsList] = useState<any[]>([]);
  const [bodyPartsLoading, setBodyPartsLoading] = useState(false);
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(
    AUTO_SELECT_BODY_PART ? bodyPart || null : null
  );

  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // po_modality is a csv for item-code-matched exams (T002) — a PET-CT order is
  // 'PT,CT'. Masters store one code per row, so resolve the csv to its lookup
  // code(s) first (hybrids resolve to their dominant modality, PT here).
  const modalityCodes = React.useMemo(
    () => resolveTemplateModalities(modality),
    [modality]
  );
  const modalityKey = modalityCodes.join(',');

  // ── Reset the selections when the panel is pointed at a DIFFERENT study ──
  // This panel stays mounted while a reporting queue advances from one patient to the next,
  // so without this the previous patient's body part and template stay selected. loadTemplates
  // only clears the template when the body part or modality actually changes — so two
  // consecutive studies of the same modality and body part would otherwise carry the previous
  // patient's template selection straight over.
  //
  // With auto-select on, this seeds the new study's own body part rather than just blanking it,
  // resolved against the list the same way the auto-select effect below does. Resolving here
  // rather than leaning on that effect matters when two consecutive studies share a body part:
  // its deps wouldn't change, so it would never run to normalise a raw value into its list code.
  // When the list is still the previous modality's, the raw value stands and that effect
  // normalises it once the new list lands.
  const loadedOrdIdRef = useRef(pacsOrdId);
  useEffect(() => {
    if (loadedOrdIdRef.current === pacsOrdId) return;
    loadedOrdIdRef.current = pacsOrdId;
    if (!AUTO_SELECT_BODY_PART) {
      setSelectedBodyPart(null);
    } else {
      const match = bodyPart
        ? bodyPartsList.find((bp: any) => bp.code === bodyPart || bp.value === bodyPart)
        : null;
      setSelectedBodyPart(match ? (match.code || match.value) : (bodyPart || null));
    }
    setSelectedTemplateId(null);
    setTemplates([]);
  }, [pacsOrdId, bodyPart]);

  // Generation counters: a reply for a study the panel has already left must never repopulate
  // the dropdowns behind the current one.
  const bodyPartsReqRef = useRef(0);
  const templatesReqRef = useRef(0);

  // Fetch body parts filtered by modality
  useEffect(() => {
    if (!modalityKey) return;
    const codes = modalityKey.split(',');
    const req = ++bodyPartsReqRef.current;
    setBodyPartsLoading(true);
    makeGetCall('/body-parts')
      .then((res: any) => {
        if (req !== bodyPartsReqRef.current) return;
        const all = res.data?.data || [];
        const filtered = all.filter((bp: any) =>
          codes.includes(String(bp.modality || '').trim().toUpperCase())
        );
        setBodyPartsList(filtered);
      })
      .catch(() => { if (req === bodyPartsReqRef.current) setBodyPartsList([]); })
      .finally(() => { if (req === bodyPartsReqRef.current) setBodyPartsLoading(false); });
  }, [modalityKey]);

  // Auto-select body part from study metadata
  useEffect(() => {
    if (!AUTO_SELECT_BODY_PART) return;
    if (bodyPart && bodyPartsList.length > 0) {
      const match = bodyPartsList.find((bp: any) => bp.code === bodyPart || bp.value === bodyPart);
      if (match) setSelectedBodyPart(match.code || match.value);
    }
  }, [bodyPart, bodyPartsList]);

  // Fetch templates when body part is selected
  const loadTemplates = useCallback((bp: string | null) => {
    if (!modalityKey || !bp) {
      // Bump the generation too, so a fetch still in flight for the previous body part
      // cannot land afterwards and refill the dropdown we just emptied.
      templatesReqRef.current++;
      setTemplates([]);
      return;
    }
    const req = ++templatesReqRef.current;
    setTemplatesLoading(true);
    setSelectedTemplateId(null);
    // owner_scope 'self' => shared/default templates + the logged-in user's own.
    // modalityKey may be a csv when the order's modalities have no single owner;
    // the backend matches any one of them.
    // pacs_ord_id lets the backend scope shared templates to the centre of the STUDY
    // being reported — it reads pacs_orders.po_center_code itself rather than trusting
    // a centre code sent from here. Inert until TEMPLATE_CENTER_SCOPING_ENABLED is on,
    // and personal templates are never centre-scoped either way.
    fetchTemplates({ modality: modalityKey, bodypart: bp, owner_scope: 'self', pacs_ord_id: pacsOrdId })
      .then((res) => { if (req === templatesReqRef.current) setTemplates(res.data?.data || []); })
      .catch(() => { if (req === templatesReqRef.current) setTemplates([]); })
      .finally(() => { if (req === templatesReqRef.current) setTemplatesLoading(false); });
  }, [modalityKey, pacsOrdId]);

  // Split into the user's own ("My Templates") vs shared defaults. Only build
  // groups when the user actually has personal templates, so the dropdown looks
  // exactly as before for everyone else.
  const templateSelectOptions = React.useMemo(() => {
    const toOption = (t: any) => ({
      label: t.dt_display_name || t.dt_template_name,
      value: t.dt_id,
    });
    const mine = templates.filter((t) => t.dt_owner_user_id != null);
    const defaults = templates.filter((t) => t.dt_owner_user_id == null);
    if (mine.length === 0) {
      return defaults.map(toOption);
    }
    const groups: any[] = [{ label: 'My Templates', options: mine.map(toOption) }];
    if (defaults.length > 0) {
      groups.push({ label: 'Default Templates', options: defaults.map(toOption) });
    }
    return groups;
  }, [templates]);

  useEffect(() => {
    loadTemplates(selectedBodyPart);
  }, [selectedBodyPart, loadTemplates]);

  const handleLoadTemplate = () => {
    if (!selectedTemplateId) {
      message.warning('Select a template first');
      return;
    }
    onInsertTemplate(selectedTemplateId);
  };

  const isPanel = variant === 'panel';

  const bodyPartSelect = (
    <Select
      placeholder="Body part"
      size="small"
      style={isPanel ? { width: '100%' } : { width: 150 }}
      value={selectedBodyPart}
      onChange={(val) => setSelectedBodyPart(val)}
      loading={bodyPartsLoading}
      allowClear
      onClear={() => { setSelectedBodyPart(null); setTemplates([]); setSelectedTemplateId(null); }}
    >
      {bodyPartsList.map((bp: any) => (
        <Select.Option key={bp.code || bp.value} value={bp.code || bp.value}>
          {bp.label}
        </Select.Option>
      ))}
    </Select>
  );

  const templateSelect = (
    <Select
      placeholder={selectedBodyPart ? (templatesLoading ? 'Loading...' : 'Select template') : 'Select body part first'}
      size="small"
      style={isPanel ? { width: '100%' } : { width: 300} }
      listHeight={400}
      popupMatchSelectWidth={false}
      value={selectedTemplateId}
      onChange={(val) => {
        setSelectedTemplateId(val ?? null);
        // Selecting a template immediately prompts the append/replace flow — no Load click.
        if (val) onInsertTemplate(val);
      }}
      disabled={!selectedBodyPart || templates.length === 0}
      loading={templatesLoading}
      allowClear
      options={templateSelectOptions}
    />
  );

  // Side-panel: vertical layout, no icon, no Load button.
  if (isPanel) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', minWidth: 0, overflow: 'hidden' }}>
        {bodyPartSelect}
        {templateSelect}
      </div>
    );
  }

  return (
    <Space size="small">
      <FileTextOutlined style={{ color: '#999' }} />
      {bodyPartSelect}
      {templateSelect}
      <Button
        size="small"
        type="primary"
        onClick={handleLoadTemplate}
        disabled={!selectedTemplateId}
      >
        Load
      </Button>
    </Space>
  );
}
